import React from 'react';
import moment from 'moment';

import { CustomerByStationService } from 'types';
import { useIntl } from 'common/config/i18n';
import { delayFormatting } from 'common/utils/date';

import FluxClientStationDisplay from './flux-client-station-display';
import { Tooltip } from 'antd';

export function useColumns() {
  const { t } = useIntl();

  const circulationDateCol = {
    title: t('table', 'CHART_DATE'),
    key: 'circulationDate',
    width: 100,
    render: (customerService: CustomerByStationService) => {
      return (
        <span style={{ color: customerService.isCancel ? 'red' : undefined }}>
          {moment(customerService.circulationDate).format('DD/MM')}
        </span>
      );
    },
  };
  const serviceNameCol = {
    title: t('table', 'CHART_TRAIN'),
    key: 'serviceName',
    width: 100,
    render: (customerService: CustomerByStationService) => {
      return <span style={{ color: customerService.isCancel ? 'red' : undefined }}>{customerService.serviceName}</span>;
    },
  };
  const stationCol = {
    title: t('table', 'CHART_STATIONS'),
    key: 'shortCodeStation',
    width: 100,
    render: (customerService: CustomerByStationService) => {
      return (
        <FluxClientStationDisplay
          customerService={customerService}
          fluxClientStationKey="shortCodeStation"
          withToolTip
          tooltipPlacement="right"
          tooltipKey="stationFullName"
        />
      );
    },
  };
  const arrivalTimeCol = {
    title: t('table', 'CHART_ARRIVAL_SCHEDULE'),
    key: 'arrivalTime',
    width: 100,
    render: (customerService: CustomerByStationService) => {
      return (
        <FluxClientStationDisplay
          customerService={customerService}
          fluxClientStationKey="arrivalTime"
          formatDisplay={(arrivalTime) => (arrivalTime ? moment(arrivalTime as string).format('HH:mm') : '')}
        />
      );
    },
  };
  const stopTimeCol = {
    title: t('table', 'CHART_TRAIN_STOP_DURATION'),
    key: 'stopTime',
    width: 100,
    render: (customerService: CustomerByStationService) => {
      return (
        <FluxClientStationDisplay
          customerService={customerService}
          fluxClientStationKey="stopTime"
          formatDisplay={(stopTime) => (stopTime ? (delayFormatting(stopTime.toString()) as string) : '')}
        />
      );
    },
  };
  const departureTimeCol = {
    title: t('table', 'CHART_DEPARTURE_SCHEDULE'),
    key: 'departureTime',
    width: 100,
    render: (customerService: CustomerByStationService) => {
      return (
        <FluxClientStationDisplay
          customerService={customerService}
          fluxClientStationKey="departureTime"
          formatDisplay={(departureTime) => (departureTime ? moment(departureTime as string).format('HH:mm') : '')}
        />
      );
    },
  };
  const trainNumberPassengersCols = {
    title: t('table', 'CHART_PASSENGER_NUMBER'),
    children: [
      {
        title: t('table', 'CHART_DESCENDING_PASSENGERS'),
        key: 'down',
        children: [
          {
            title: t('table', 'CHART_DESCENDING_PASSENGERS_1'),
            key: 'firstTrainNbPassengersDown',
            width: 100,
            render: (customerService: CustomerByStationService) => {
              return (
                <FluxClientStationDisplay
                  customerService={customerService}
                  fluxClientStationKey="firstTrainNbPassengersDown"
                  formatDisplay={(firstDown) => (firstDown ? firstDown.toString() : '0')}
                />
              );
            },
          },
          {
            title: t('table', 'CHART_DESCENDING_PASSENGERS_2'),
            key: 'secondTrainNbPassengersDown',
            width: 100,
            render: (customerService: CustomerByStationService) => {
              return (
                <FluxClientStationDisplay
                  customerService={customerService}
                  fluxClientStationKey="secondTrainNbPassengersDown"
                  formatDisplay={(secondDown) => (secondDown ? secondDown.toString() : '0')}
                />
              );
            },
          },
        ],
      },
      {
        title: t('table', 'CHART_ASCENDING_PASSENGERS'),
        key: 'up',
        children: [
          {
            title: t('table', 'CHART_ASCENDING_PASSENGERS_1'),
            key: 'firstTrainNbPassengersUp',
            width: 100,
            render: (customerService: CustomerByStationService) => {
              return (
                <FluxClientStationDisplay
                  customerService={customerService}
                  fluxClientStationKey="firstTrainNbPassengersUp"
                  formatDisplay={(firstUp) => (firstUp ? firstUp.toString() : '0')}
                />
              );
            },
          },
          {
            title: t('table', 'CHART_ASCENDING_PASSENGERS_2'),
            key: 'secondTrainNbPassengersUp',
            width: 100,
            render: (customerService: CustomerByStationService) => {
              return (
                <FluxClientStationDisplay
                  customerService={customerService}
                  fluxClientStationKey="secondTrainNbPassengersUp"
                  formatDisplay={(secondUp) => (secondUp ? secondUp.toString() : '0')}
                />
              );
            },
          },
        ],
      },
      {
        title: t('table', 'CHART_SUM'),
        key: 'total',
        children: [
          {
            title: t('table', 'CHART_SUM_1'),
            key: 'firstTrainNbPassengersTotal',
            width: 100,
            render: (customerService: CustomerByStationService) => {
              return (
                <FluxClientStationDisplay
                  customerService={customerService}
                  fluxClientStationKey="firstTrainNbPassengersTotal"
                  formatDisplay={(firstTotal) => (firstTotal ? firstTotal.toString() : '0')}
                />
              );
            },
          },
          {
            title: t('table', 'CHART_SUM_2'),
            key: 'secondTrainNbPassengersTotal',
            width: 100,
            render: (customerService: CustomerByStationService) => {
              return (
                <FluxClientStationDisplay
                  customerService={customerService}
                  fluxClientStationKey="secondTrainNbPassengersTotal"
                  formatDisplay={(secondTotal) => (secondTotal ? secondTotal.toString() : '0')}
                />
              );
            },
          },
          {
            title: (
              <Tooltip title={t('table', 'CHART_SUM_3_TOOLTIP')} placement="top">
                {t('table', 'CHART_SUM_3')}
              </Tooltip>
            ),
            key: 'allTrainNbPassengers',
            width: 100,
            render: (customerService: CustomerByStationService) => {
              return (
                <FluxClientStationDisplay
                  customerService={customerService}
                  fluxClientStationKey="allTrainNbPassengers"
                  formatDisplay={(allTotal) => (allTotal ? allTotal.toString() : '0')}
                />
              );
            },
          },
        ],
      },
    ],
  };
  const trainUfrCol = {
    title: t('table', 'CHART_PWD'),
    children: [
      {
        title: t('table', 'CHART_DESCENDING_PASSENGERS'),
        key: 'allTrainUfrDown',
        width: 100,
        render: (customerService: CustomerByStationService) => {
          return (
            <FluxClientStationDisplay
              customerService={customerService}
              fluxClientStationKey="allTrainUfrDown"
              formatDisplay={(ufrDown) => (ufrDown ? ufrDown.toString() : '0')}
            />
          );
        },
      },
      {
        title: t('table', 'CHART_ASCENDING_PASSENGERS'),
        key: 'allTrainUfrUp',
        width: 100,
        render: (customerService: CustomerByStationService) => {
          return (
            <FluxClientStationDisplay
              customerService={customerService}
              fluxClientStationKey="allTrainUfrUp"
              formatDisplay={(ufrUp) => (ufrUp ? ufrUp.toString() : '0')}
            />
          );
        },
      },
      {
        title: t('table', 'CHART_SUM'),
        key: 'allTrainUfrTotal',
        width: 100,
        render: (customerService: CustomerByStationService) => {
          return (
            <FluxClientStationDisplay
              customerService={customerService}
              fluxClientStationKey="allTrainUfrTotal"
              formatDisplay={(ufrTotal) => (ufrTotal ? ufrTotal.toString() : '0')}
            />
          );
        },
      },
    ],
  };
  const trainChildCol = {
    title: t('table', 'CHART_CHILDREN'),
    children: [
      {
        title: t('table', 'CHART_DESCENDING_PASSENGERS'),
        key: 'allTrainChildDown',
        width: 100,
        render: (customerService: CustomerByStationService) => {
          return (
            <FluxClientStationDisplay
              customerService={customerService}
              fluxClientStationKey="allTrainChildDown"
              formatDisplay={(childDown) => (childDown ? childDown.toString() : '0')}
            />
          );
        },
      },
      {
        title: t('table', 'CHART_ASCENDING_PASSENGERS'),
        key: 'allTrainChildUp',
        width: 100,
        render: (customerService: CustomerByStationService) => {
          return (
            <FluxClientStationDisplay
              customerService={customerService}
              fluxClientStationKey="allTrainChildUp"
              formatDisplay={(childUp) => (childUp ? childUp.toString() : '0')}
            />
          );
        },
      },
      {
        title: t('table', 'CHART_SUM'),
        key: 'allTrainChildTotal',
        width: 100,
        render: (customerService: CustomerByStationService) => {
          return (
            <FluxClientStationDisplay
              customerService={customerService}
              fluxClientStationKey="allTrainChildTotal"
              formatDisplay={(childTotal) => (childTotal ? childTotal.toString() : '0')}
            />
          );
        },
      },
    ],
  };

  const Columns_FR = [
    { ...circulationDateCol },
    { ...serviceNameCol },
    { ...stationCol },
    { ...arrivalTimeCol },
    { ...stopTimeCol },
    { ...departureTimeCol },
    { ...trainNumberPassengersCols },
    { ...trainChildCol },
    { ...trainUfrCol },
  ];

  const Columns_ES = [
    { ...circulationDateCol },
    { ...serviceNameCol },
    { ...stationCol },
    { ...arrivalTimeCol },
    { ...stopTimeCol },
    { ...departureTimeCol },
    { ...trainNumberPassengersCols },
    {
      title: (
        <>
          <div>{t('table', 'HART_PWD1')}</div>
          <div>{t('table', 'HART_PWD0')}</div>
        </>
      ),
      children: [
        {
          title: t('table', 'HART_PWD3'),
          key: 'allTrainAssistanceDown',
          width: 100,
          render: (customerService: CustomerByStationService) => {
            return (
              <FluxClientStationDisplay
                customerService={customerService}
                fluxClientStationKey="allTrainAssistanceDown"
                formatDisplay={(assistanceDown) => (assistanceDown ? assistanceDown.toString() : '0')}
              />
            );
          },
        },
        {
          title: t('table', 'HART_PWD2'),
          key: 'allTrainAssistanceUp',
          width: 100,
          render: (customerService: CustomerByStationService) => {
            return (
              <FluxClientStationDisplay
                customerService={customerService}
                fluxClientStationKey="allTrainAssistanceUp"
                formatDisplay={(assistanceUp) => (assistanceUp ? assistanceUp.toString() : '0')}
              />
            );
          },
        },
        {
          title: t('table', 'CHART_SUM'),
          key: 'allTrainAssistanceTotal',
          width: 100,
          render: (customerService: CustomerByStationService) => {
            return (
              <FluxClientStationDisplay
                customerService={customerService}
                fluxClientStationKey="allTrainAssistanceTotal"
                formatDisplay={(assistanceTotal) => (assistanceTotal ? assistanceTotal.toString() : '0')}
              />
            );
          },
        },
      ],
    },
    { ...trainUfrCol },
    { ...trainChildCol },
    {
      title: t('table', 'CHART_BAMB'),
      children: [
        {
          title: t('table', 'CHART_DESCENDING_PASSENGERS'),
          key: 'allTrainBabyDown',
          width: 100,
          render: (customerService: CustomerByStationService) => {
            return (
              <FluxClientStationDisplay
                customerService={customerService}
                fluxClientStationKey="allTrainBabyDown"
                formatDisplay={(babyDown) => (babyDown ? babyDown.toString() : '0')}
              />
            );
          },
        },
        {
          title: t('table', 'CHART_ASCENDING_PASSENGERS'),
          key: 'allTrainBabyUp',
          width: 100,
          render: (customerService: CustomerByStationService) => {
            return (
              <FluxClientStationDisplay
                customerService={customerService}
                fluxClientStationKey="allTrainBabyUp"
                formatDisplay={(babyUp) => (babyUp ? babyUp.toString() : '0')}
              />
            );
          },
        },
        {
          title: t('table', 'CHART_SUM'),
          key: 'allTrainBabyTotal',
          width: 100,
          render: (customerService: CustomerByStationService) => {
            return (
              <FluxClientStationDisplay
                customerService={customerService}
                fluxClientStationKey="allTrainBabyTotal"
                formatDisplay={(babyTotal) => (babyTotal ? babyTotal.toString() : '0')}
              />
            );
          },
        },
      ],
    },
  ];

  return {
    FR: Columns_FR,
    ES: Columns_ES,
  };
}
